<template>
    <div :class="$style.container">
        <!-- 통계 탭 네비게이션 -->
        <StatsTabs />

        <!-- 로딩 상태 -->
        <div v-if="loading">
            <LoadingScreen loading-text="찜 통계를 불러오는 중..." />
        </div>

        <!-- 에러 상태 -->
        <div v-else-if="error" :class="$style.error">
            <p>{{ error }}</p>
            <button @click="fetchLikeStats" :class="$style.retryButton">다시 시도</button>
        </div>

        <!-- 통계 데이터 -->
        <div v-else>
            <div :class="$style.header">
                <h1 :class="$style.title">💖 찜 통계</h1>
                <p :class="$style.subtitle">아티스트, 공연, 게시글에 대한 찜/좋아요 현황</p>
            </div>

            <!-- 요약 정보 -->
            <div :class="$style.summary">
                <div :class="$style.summaryCard">
                    <h3>총 아티스트 찜</h3>
                    <p :class="$style.summaryNumber">{{ totalArtistLikes }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>총 공연 찜</h3>
                    <p :class="$style.summaryNumber">{{ totalLiveLikes }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>찜한 사용자 수</h3>
                    <p :class="$style.summaryNumber">{{ usersWithLikes }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>찜 받은 아티스트</h3>
                    <p :class="$style.summaryNumber">{{ artistsWithLikes }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>찜 받은 공연</h3>
                    <p :class="$style.summaryNumber">{{ livesWithLikes }}</p>
                </div>
            </div>

            <!-- 탭 선택 -->
            <div :class="$style.tabContainer">
                <button v-for="tab in tabOptions" :key="tab.value"
                    :class="[$style.tabButton, { [$style.activeTabBtn]: currentTab === tab.value }]"
                    @click="currentTab = tab.value">
                    {{ tab.label }}
                </button>
            </div>

            <!-- 인기 아티스트 랭킹 -->
            <div v-if="currentTab === 'artists'" :class="$style.rankingSection">
                <div :class="$style.sectionHeader">
                    <h2>🎵 인기 아티스트 TOP</h2>
                    <div :class="$style.controls">
                        <select v-model="artistLimit" :class="$style.select">
                            <option :value="0">전체 보기</option>
                            <option :value="10">TOP 10</option>
                            <option :value="20">TOP 20</option>
                            <option :value="50">TOP 50</option>
                        </select>
                    </div>
                </div>
                <div :class="$style.rankingList">
                    <div v-for="(artist, index) in topArtists" :key="artist.name"
                        :class="[$style.rankingItem, { [$style.topThree]: index < 3 }]">
                        <div :class="$style.rank">
                            <span v-if="index === 0" :class="$style.medal">🥇</span>
                            <span v-else-if="index === 1" :class="$style.medal">🥈</span>
                            <span v-else-if="index === 2" :class="$style.medal">🥉</span>
                            <span v-else :class="$style.rankNumber">{{ index + 1 }}</span>
                        </div>
                        <div :class="$style.itemInfo">
                            <div :class="$style.itemAvatar">
                                <img v-if="artist.profileImage" :src="artist.profileImage" :alt="artist.name"
                                    @error="handleImageError" />
                                <div v-else :class="$style.defaultAvatar">🎤</div>
                            </div>
                            <div :class="$style.itemDetails">
                                <h3 :class="$style.itemName">{{ artist.name }}</h3>
                                <p :class="$style.itemMeta">{{ getArtistType(artist) }}</p>
                            </div>
                        </div>
                        <div :class="$style.likeCount">
                            <span :class="$style.heartIcon">💖</span>
                            <span :class="$style.count">{{ artist.likeCount }}</span>
                        </div>
                    </div>
                    <div v-if="topArtists.length === 0" :class="$style.empty">
                        찜 받은 아티스트가 없습니다.
                    </div>
                </div>
            </div>

            <!-- 인기 공연 랭킹 -->
            <div v-if="currentTab === 'lives'" :class="$style.rankingSection">
                <div :class="$style.sectionHeader">
                    <h2>🎪 인기 공연 TOP</h2>
                    <div :class="$style.controls">
                        <select v-model="liveLimit" :class="$style.select">
                            <option :value="0">전체 보기</option>
                            <option :value="10">TOP 10</option>
                            <option :value="20">TOP 20</option>
                            <option :value="50">TOP 50</option>
                        </select>
                    </div>
                </div>
                <div :class="$style.rankingList">
                    <div v-for="(live, index) in topLives" :key="live.id"
                        :class="[$style.rankingItem, { [$style.topThree]: index < 3 }]">
                        <div :class="$style.rank">
                            <span v-if="index === 0" :class="$style.medal">🥇</span>
                            <span v-else-if="index === 1" :class="$style.medal">🥈</span>
                            <span v-else-if="index === 2" :class="$style.medal">🥉</span>
                            <span v-else :class="$style.rankNumber">{{ index + 1 }}</span>
                        </div>
                        <div :class="$style.itemInfo">
                            <div :class="$style.itemAvatar">
                                <img v-if="live.imageUrl" :src="live.imageUrl" :alt="live.title"
                                    @error="handleImageError" />
                                <div v-else :class="$style.defaultAvatar">🎪</div>
                            </div>
                            <div :class="$style.itemDetails">
                                <h3 :class="$style.itemName">{{ live.title }}</h3>
                                <p :class="$style.itemMeta">
                                    {{ live.place || '장소 미정' }} · {{ formatDate(live.date) }}
                                </p>
                            </div>
                        </div>
                        <div :class="$style.likeCount">
                            <span :class="$style.heartIcon">💖</span>
                            <span :class="$style.count">{{ live.likes || 0 }}</span>
                        </div>
                    </div>
                    <div v-if="topLives.length === 0" :class="$style.empty">
                        찜 받은 공연이 없습니다.
                    </div>
                </div>
            </div>

            <!-- 찜을 많이 한 사용자 -->
            <div v-if="currentTab === 'users'" :class="$style.rankingSection">
                <div :class="$style.sectionHeader">
                    <h2>👥 활동적인 팬 TOP</h2>
                    <div :class="$style.controls">
                        <select v-model="userLimit" :class="$style.select">
                            <option :value="0">전체 보기</option>
                            <option :value="10">TOP 10</option>
                            <option :value="20">TOP 20</option>
                            <option :value="50">TOP 50</option>
                        </select>
                    </div>
                </div>
                <div :class="$style.rankingList">
                    <div v-for="(user, index) in topUsers" :key="user.email"
                        :class="[$style.rankingItem, { [$style.topThree]: index < 3 }]">
                        <div :class="$style.rank">
                            <span v-if="index === 0" :class="$style.medal">🥇</span>
                            <span v-else-if="index === 1" :class="$style.medal">🥈</span>
                            <span v-else-if="index === 2" :class="$style.medal">🥉</span>
                            <span v-else :class="$style.rankNumber">{{ index + 1 }}</span>
                        </div>
                        <div :class="$style.itemInfo">
                            <div :class="$style.itemAvatar">
                                <img v-if="user.profileImage" :src="user.profileImage" :alt="user.nickname"
                                    @error="handleImageError" />
                                <div v-else :class="$style.defaultAvatar">👤</div>
                            </div>
                            <div :class="$style.itemDetails">
                                <h3 :class="$style.itemName">{{ user.nickname || '익명' }}</h3>
                                <p :class="$style.itemMeta">
                                    아티스트 {{ user.artistLikeCount }}개 · 공연 {{ user.liveLikeCount }}개
                                </p>
                                <p :class="$style.itemSubMeta">
                                    가입일: {{ formatDate(user.joinDate) }}
                                </p>
                                <p :class="$style.itemSubMeta">
                                    최근 로그인: {{ formatDate(user.lastLogin) }}
                                </p>
                                <p :class="$style.itemSubMeta">
                                    최근 업데이트: {{ formatDate(user.updatedAt) }}
                                </p>
                            </div>
                        </div>
                        <div :class="$style.likeCount">
                            <span :class="$style.heartIcon">💖</span>
                            <span :class="$style.count">{{ user.totalLikes }}</span>
                        </div>
                    </div>
                    <div v-if="topUsers.length === 0" :class="$style.empty">
                        찜한 사용자가 없습니다.
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getFirestore, collection, getDocs } from "firebase/firestore";
import StatsTabs from "@/components/StatsTabs.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";

export default {
    name: "LikeStats",
    components: {
        StatsTabs,
        LoadingScreen,
    },
    data() {
        return {
            loading: false,
            error: null,
            users: [],
            artists: [],
            lives: [],
            currentTab: "artists",
            artistLimit: 0,
            liveLimit: 0,
            userLimit: 0,
            tabOptions: [
                { value: "artists", label: "인기 아티스트" },
                { value: "lives", label: "인기 공연" },
                { value: "users", label: "활동적인 팬" },
            ],
        };
    },
    computed: {
        // 요약 통계
        totalArtistLikes() {
            return this.users.reduce((sum, user) => sum + (user.artistLikes?.length || 0), 0);
        },
        totalLiveLikes() {
            return this.users.reduce((sum, user) => sum + (user.liveLikes?.length || 0), 0);
        },
        usersWithLikes() {
            return this.users.filter(
                (user) =>
                    (user.artistLikes?.length || 0) > 0 || (user.liveLikes?.length || 0) > 0
            ).length;
        },
        artistsWithLikes() {
            return this.artists.filter((artist) => (artist.likedUsers?.length || 0) > 0).length;
        },
        livesWithLikes() {
            return this.lives.filter((live) => (live.likes || 0) > 0).length;
        },

        // 인기 아티스트 TOP
        topArtists() {
            const sorted = [...this.artists]
                .map((artist) => ({
                    ...artist,
                    likeCount: artist.likedUsers?.length || 0,
                }))
                .filter((artist) => artist.likeCount > 0)
                .sort((a, b) => b.likeCount - a.likeCount);
            return this.artistLimit > 0 ? sorted.slice(0, this.artistLimit) : sorted;
        },

        // 인기 공연 TOP
        topLives() {
            const sorted = [...this.lives]
                .filter((live) => (live.likes || 0) > 0)
                .sort((a, b) => (b.likes || 0) - (a.likes || 0));
            return this.liveLimit > 0 ? sorted.slice(0, this.liveLimit) : sorted;
        },

        // 찜을 많이 한 사용자 TOP
        topUsers() {
            const sorted = [...this.users]
                .map((user) => ({
                    ...user,
                    artistLikeCount: user.artistLikes?.length || 0,
                    liveLikeCount: user.liveLikes?.length || 0,
                    totalLikes: (user.artistLikes?.length || 0) + (user.liveLikes?.length || 0),
                }))
                .filter((user) => user.totalLikes > 0)
                .sort((a, b) => b.totalLikes - a.totalLikes);
            return this.userLimit > 0 ? sorted.slice(0, this.userLimit) : sorted;
        },
    },
    async mounted() {
        await this.fetchLikeStats();
    },
    methods: {
        async fetchLikeStats() {
            this.loading = true;
            this.error = null;

            try {
                const db = getFirestore();

                // 사용자 데이터 가져오기
                const { getAllUsers } = await import("@/utils/read/user");
                this.users = await getAllUsers();

                // 아티스트 데이터 가져오기
                const artistsSnapshot = await getDocs(collection(db, "artists"));
                this.artists = artistsSnapshot.docs.map((doc) => ({
                    name: doc.id,
                    ...doc.data(),
                }));

                // 공연 데이터 가져오기
                const livesSnapshot = await getDocs(collection(db, "lives"));
                this.lives = livesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
            } catch (error) {
                console.error("찜 통계 로딩 오류:", error);
                this.error = "찜 통계를 불러오는데 실패했습니다.";
            } finally {
                this.loading = false;
            }
        },

        formatDate(date) {
            if (!date) return "날짜 미정";

            try {
                let d;
                if (date.toDate && typeof date.toDate === "function") {
                    d = date.toDate();
                } else if (typeof date === "object" && date.seconds) {
                    d = new Date(date.seconds * 1000);
                } else if (typeof date === "string") {
                    d = new Date(date);
                } else {
                    d = new Date(date);
                }

                if (isNaN(d.getTime())) return "날짜 미정";

                return d.toLocaleDateString("ko-KR", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                });
            } catch {
                return "날짜 미정";
            }
        },

        handleImageError(event) {
            event.target.style.display = "none";
        },

        getArtistType(artist) {
            const typeLabels = {
                vocal: "보컬",
                guitar: "기타",
                keyboard: "키보드",
                drum: "드럼",
                bass: "베이스",
                dance: "댄스",
                band: "밴드",
                other: "그 외",
                mix: "그 외",
            };

            if (artist.category === "band") {
                return "밴드";
            }

            if (Array.isArray(artist.type) && artist.type.length > 0) {
                return artist.type
                    .map((t) => typeLabels[t] || t)
                    .join(", ");
            }

            if (artist.type && typeof artist.type === "string") {
                return typeLabels[artist.type] || artist.type;
            }

            return "타입 미정";
        },
    },
};
</script>

<style module>
.container {
    width: 100%;
    max-width: 1024px;
    padding: 0 15px;
    margin: 0 auto;
}

.error {
    text-align: center;
    padding: 40px;
    color: #d32f2f;
}

.retryButton {
    background: #2196f3;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    margin-top: 10px;
}

.retryButton:hover {
    background: #1976d2;
}

.header {
    text-align: center;
    margin-bottom: 25px;
    padding: 15px 0;
}

.title {
    font-size: 2.2rem;
    font-weight: bold;
    color: #333;
    margin: 0 0 10px 0;
}

.subtitle {
    font-size: 1rem;
    color: #666;
    margin: 0;
}

.summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 20px;
}

.summaryCard {
    background: white;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    text-align: center;
    border: 1px solid #e0e0e0;
}

.summaryCard h3 {
    margin: 0 0 12px 0;
    font-size: 0.9rem;
    color: #666;
    font-weight: 600;
}

.summaryNumber {
    font-size: 1.8rem;
    font-weight: bold;
    color: #ff6b9d;
    margin: 0;
}

/* 탭 컨테이너 */
.tabContainer {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    background: white;
    padding: 10px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.tabButton {
    flex: 1;
    padding: 12px 20px;
    border: 1px solid #e0e0e0;
    background: #f8f9fa;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: all 0.2s;
}

.tabButton:hover {
    background: #f0f0f0;
    border-color: #ccc;
}

.activeTabBtn {
    background: linear-gradient(135deg, #ff6b9d, #ff8fab);
    color: white;
    border-color: #ff6b9d;
}

.activeTabBtn:hover {
    background: linear-gradient(135deg, #ff5a8f, #ff7fa0);
    border-color: #ff5a8f;
}

/* 랭킹 섹션 */
.rankingSection {
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 30px;
}

.sectionHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 20px;
    border-bottom: 1px solid #e0e0e0;
    background: #f8f9fa;
}

.sectionHeader h2 {
    margin: 0;
    color: #333;
    font-size: 1.2rem;
}

.controls {
    display: flex;
    gap: 8px;
    align-items: center;
}

.select {
    padding: 7px 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: white;
    color: #333;
    font-size: 13px;
    min-width: 100px;
}

/* 랭킹 리스트 */
.rankingList {
    padding: 10px 20px;
}

.rankingItem {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 15px 10px;
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.2s;
}

.rankingItem:last-child {
    border-bottom: none;
}

.rankingItem:hover {
    background: #fafafa;
}

.topThree {
    background: linear-gradient(135deg, #fff9fb, #fff5f8);
}

.topThree:hover {
    background: linear-gradient(135deg, #fff5f8, #fff0f5);
}

.rank {
    width: 40px;
    text-align: center;
    flex-shrink: 0;
}

.medal {
    font-size: 1.5rem;
}

.rankNumber {
    font-size: 1.1rem;
    font-weight: bold;
    color: #888;
}

.itemInfo {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.itemAvatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.itemAvatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.defaultAvatar {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #ff6b9d, #ff8fab);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
}

.itemDetails {
    flex: 1;
    min-width: 0;
}

.itemName {
    margin: 0 0 4px 0;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.itemMeta {
    margin: 0;
    font-size: 0.85rem;
    color: #888;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.itemSubMeta {
    margin: 4px 0 0 0;
    font-size: 0.75rem;
    color: #aaa;
}

.likeCount {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: linear-gradient(135deg, #fff0f5, #ffe5ee);
    border-radius: 20px;
    flex-shrink: 0;
}

.heartIcon {
    font-size: 1rem;
}

.count {
    font-size: 1rem;
    font-weight: bold;
    color: #ff6b9d;
}

.empty {
    text-align: center;
    padding: 40px;
    color: #999;
    font-size: 0.95rem;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
    .container {
        width: 100%;
        padding: 0 10px;
    }

    .header {
        margin-bottom: 20px;
        padding: 10px 0;
    }

    .title {
        font-size: 1.7rem;
        margin-bottom: 6px;
    }

    .subtitle {
        font-size: 0.9rem;
    }

    .summary {
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        margin-bottom: 20px;
    }

    .summaryCard {
        padding: 15px 10px;
        border-radius: 8px;
    }

    .summaryCard h3 {
        font-size: 0.75rem;
        margin-bottom: 8px;
    }

    .summaryNumber {
        font-size: 1.4rem;
    }

    .tabContainer {
        flex-direction: column;
        gap: 8px;
        padding: 10px;
    }

    .tabButton {
        padding: 12px 15px;
        font-size: 0.9rem;
    }

    .rankingSection {
        border-radius: 8px;
    }

    .sectionHeader {
        flex-direction: column;
        gap: 12px;
        align-items: stretch;
        padding: 15px;
    }

    .sectionHeader h2 {
        font-size: 1.1rem;
        text-align: center;
    }

    .controls {
        width: 100%;
    }

    .select {
        width: 100%;
        padding: 10px 12px;
        font-size: 13px;
    }

    .rankingList {
        padding: 10px 15px;
    }

    .rankingItem {
        gap: 10px;
        padding: 12px 5px;
    }

    .rank {
        width: 30px;
    }

    .medal {
        font-size: 1.2rem;
    }

    .rankNumber {
        font-size: 1rem;
    }

    .itemAvatar {
        width: 40px;
        height: 40px;
    }

    .defaultAvatar {
        font-size: 1.1rem;
    }

    .itemName {
        font-size: 0.9rem;
    }

    .itemMeta {
        font-size: 0.75rem;
    }

    .itemSubMeta {
        font-size: 0.65rem;
    }

    .likeCount {
        padding: 6px 10px;
        gap: 4px;
    }

    .heartIcon {
        font-size: 0.85rem;
    }

    .count {
        font-size: 0.9rem;
    }
}
</style>
